import * as THREE from "three";
import { createMaterials, type MaterialMap } from "./Materials";
import {
    DEFAULT_ORTHO_FRUSTUM,
    CAMERA_NEAR,
    CAMERA_FAR,
    AMBIENT_INTENSITY,
    SUN_INTENSITY,
    SUN_POSITION,
    MIN_RENDERER_SIZE,
    RENDERER_MAX_PIXEL_RATIO,
} from "../config";

export class SceneManager {
    container: HTMLElement;
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    materials: MaterialMap;
    /** All disposable geometries owned by this scene. The renderer adds its own. */
    sharedGeometries: Set<THREE.BufferGeometry>;
    /** All disposable materials owned by this scene. */
    sharedMaterials: Set<THREE.Material>;
    group: THREE.Group;
    private lights: THREE.Light[] = [];
    private resizeHandler: () => void;

    constructor(container: HTMLElement) {
        this.container = container;
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            premultipliedAlpha: false,
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDERER_MAX_PIXEL_RATIO));
        // Fully transparent clear so the canvas shows whatever is behind it,
        // never a sky band from default WebGL clear.
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setClearAlpha(0);

        // Initial sizing before scene/camera exist; can't render yet.
        const initWidth = container.clientWidth || MIN_RENDERER_SIZE;
        const initHeight = container.clientHeight || MIN_RENDERER_SIZE;
        this.renderer.setSize(initWidth, initHeight, false);
        this.renderer.domElement.style.width = "100%";
        this.renderer.domElement.style.height = "100%";

        container.appendChild(this.renderer.domElement);

        this.scene = new THREE.Scene();
        this.scene.background = null;

        const aspect = initWidth / initHeight;
        const frustum = DEFAULT_ORTHO_FRUSTUM;
        this.camera = new THREE.OrthographicCamera(
            -frustum * aspect,
            frustum * aspect,
            frustum,
            -frustum,
            CAMERA_NEAR,
            CAMERA_FAR,
        );

        this.applyTheme();

        const { map, geometries } = createMaterials();
        this.materials = map;
        this.sharedGeometries = new Set(geometries);
        this.sharedMaterials = new Set(Object.values(map).map((e) => e.material));

        this.group = new THREE.Group();
        this.scene.add(this.group);

        // Resize handler runs only after the scene/camera are constructed.
        // setSize clears the drawing buffer; recompute aspect and re-render
        // so window resizes don't leave a blank canvas.
        this.resizeHandler = () => {
            const width = container.clientWidth || MIN_RENDERER_SIZE;
            const height = container.clientHeight || MIN_RENDERER_SIZE;
            this.renderer.setSize(width, height, false);
            this.renderer.domElement.style.width = "100%";
            this.renderer.domElement.style.height = "100%";
            const a = width / height;
            const halfH = (this.camera.top - this.camera.bottom) / 2;
            const halfW = halfH * a;
            this.camera.left = -halfW;
            this.camera.right = halfW;
            this.camera.updateProjectionMatrix();
            this.renderer.render(this.scene, this.camera);
        };
        window.addEventListener("resize", this.resizeHandler);
    }

    render() {
        this.renderer.render(this.scene, this.camera);
    }

    applyTheme() {
        this.lights.forEach((l) => this.scene.remove(l));
        this.lights = [];

        const isLight =
            typeof document !== "undefined" &&
            document.documentElement.getAttribute("data-theme") === "light";
        const ambientMul = isLight ? 1.55 : 1.0;
        const sunMul = isLight ? 1.2 : 1.0;
        const sun = new THREE.DirectionalLight(0xffffff, SUN_INTENSITY * sunMul);
        sun.position.set(...SUN_POSITION);
        this.lights.push(new THREE.AmbientLight(0xffffff, AMBIENT_INTENSITY * ambientMul), sun);
        if (isLight) {
            this.lights.push(new THREE.HemisphereLight(0xffffff, 0xb0c4d0, 0.55));
        }
        this.lights.forEach((l) => this.scene.add(l));
    }

    clearWorld() {
        for (const child of [...this.group.children]) {
            this.group.remove(child);
            const mesh = child as THREE.Mesh;
            if (mesh.userData.terrainOwnedGeometry && mesh.geometry) {
                this.sharedGeometries.delete(mesh.geometry);
                mesh.geometry.dispose();
            }
            const instancedMesh = child as THREE.InstancedMesh;
            if (instancedMesh.isInstancedMesh) instancedMesh.dispose();
        }
    }

    dispose() {
        this.clearWorld();
        window.removeEventListener("resize", this.resizeHandler);

        this.sharedGeometries.forEach((g) => g.dispose());
        this.sharedMaterials.forEach((m) => m.dispose());

        this.scene.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh) return;
            if (mesh.geometry && !this.sharedGeometries.has(mesh.geometry)) mesh.geometry.dispose();
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((m) => { if (!this.sharedMaterials.has(m)) m.dispose(); });
            } else if (mesh.material && !this.sharedMaterials.has(mesh.material as THREE.Material)) {
                (mesh.material as THREE.Material).dispose();
            }
        });

        this.scene.clear();
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        if (this.renderer.domElement.parentNode === this.container) {
            this.container.removeChild(this.renderer.domElement);
        }
    }
}
